import { useState, useEffect, useRef } from 'react'
import styles from './AvatarCropModal.module.css'

interface Props {
  file: File
  onSave: (dataUrl: string) => Promise<void>
  onCancel: () => void
}

const OUTPUT_SIZE = 300
const MIN_ZOOM = 1
const MAX_ZOOM = 3

export function AvatarCropModal({ file, onSave, onCancel }: Props) {
  const [objectUrl, setObjectUrl] = useState('')
  const [zoom, setZoom] = useState(1)
  const [saving, setSaving] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setObjectUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  async function handleSave() {
    const img = imgRef.current
    if (!img || !objectUrl) return
    setSaving(true)
    try {
      const canvas = document.createElement('canvas')
      canvas.width = OUTPUT_SIZE
      canvas.height = OUTPUT_SIZE
      const ctx = canvas.getContext('2d')!
      ctx.beginPath()
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2)
      ctx.clip()
      const coverScale = Math.max(OUTPUT_SIZE / img.naturalWidth, OUTPUT_SIZE / img.naturalHeight)
      const drawScale = coverScale * zoom
      const drawW = img.naturalWidth * drawScale
      const drawH = img.naturalHeight * drawScale
      const x = (OUTPUT_SIZE - drawW) / 2
      const y = (OUTPUT_SIZE - drawH) / 2
      ctx.drawImage(img, x, y, drawW, drawH)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
      await onSave(dataUrl)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div className={styles.modal}>
        <p className={styles.title}>Adjust profile picture</p>
        <div className={styles.previewWrapper}>
          <div className={styles.previewCircle}>
            {objectUrl && (
              <img
                ref={imgRef}
                src={objectUrl}
                alt="Preview"
                className={styles.previewImg}
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            )}
          </div>
        </div>
        <div className={styles.sliderRow}>
          <span className={styles.sliderLabel}>Zoom</span>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className={styles.slider}
          />
        </div>
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
